/**
 * @graphql cc
 */

import { graphql } from 'react-relay';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { reportError } from '../reportError.ts';

import type {
	blockServiceSnippetSharingQuery$data,
	blockServiceSnippetSharingQuery$variables,
} from './__generated__/blockServiceSnippetSharingQuery.graphql.ts';

/**
 * Fetches site configuration and returns whether snippet sharing for the site is enabled.
 * Used by fetchActions when options are not provided (e.g. first fetch from SnippetsProvider);
 * the result is passed to the client and stored in the snippets store.
 */
export const getIsSnippetSharingForSiteEnabled = async (): Promise<boolean> => {
	try {
		const { data } = await queryInterop<
			blockServiceSnippetSharingQuery$data,
			blockServiceSnippetSharingQuery$variables
		>({
			query: graphql`
				query blockServiceSnippetSharingQuery {
					confluence {
						siteConfiguration {
							isSnippetSharingForSiteEnabled
						}
					}
				}
			`,
			variables: {},
		});

		return data?.confluence?.siteConfiguration?.isSnippetSharingForSiteEnabled === true;
	} catch (error) {
		logFetchError(error);
		markErrorAsHandled(error);
		return false;
	}
};

const logFetchError = (maybeError: unknown) => {
	const error = maybeError instanceof Error ? maybeError : new Error(String(maybeError));
	reportError(
		new Error(
			`Error when fetching snippet sharing for site configuration: ${error.message || String(error)}`,
		),
	);
};
